import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { gradeToGradeBand, GradeBand, UrgencyTier } from '@peekapak/types/EmotionsClassification';
import {
  DateRange,
  EmotionsReportCheckin,
  EmotionsReportClassroom,
  deriveEmotionsReportRow,
  hasQualifyingCheckinInRange,
} from '@peekapak/types/EmotionsReportRow';

export interface EmotionsReportDisplayRow {
  userId: string;
  name: string;
  classroomId: string;
  classroomName: string;
  teacherName: string;
  grade: number;
  gradeBand: GradeBand;
  recentFeelings: (Mood | SecondaryMood)[];
  downCount: number;
  totalCount: number;
  last30DaysLowCount: number;
  urgencyTier: UrgencyTier | null;
  checkins: EmotionsReportCheckin[];
}

const TIER_SEVERITY: Record<UrgencyTier, number> = {
  urgent: 3,
  concerning: 2,
  emerging: 1,
};

function severityOf(tier: UrgencyTier | null): number {
  return tier ? TIER_SEVERITY[tier] : 0;
}

export function buildReportRows(
  classrooms: EmotionsReportClassroom[],
  selectedRange: DateRange,
  now: number = Date.now(),
): EmotionsReportDisplayRow[] {
  const rows = classrooms.flatMap((classroom) => {
    const gradeBand = gradeToGradeBand(classroom.grade);
    return classroom.students.map((student) => {
      const derived = deriveEmotionsReportRow(student.checkins, gradeBand, student.emotionsUrgency, selectedRange, now);

      return {
        userId: student.userId,
        name: `${student.firstName} ${student.lastName}`.trim(),
        classroomId: classroom.classroomId,
        classroomName: classroom.classroomName,
        teacherName: classroom.teacherName,
        grade: student.grade,
        gradeBand,
        checkins: student.checkins,
        ...derived,
      };
    });
  });

  const visibleRows = rows.filter(
    (row) => hasQualifyingCheckinInRange(row.checkins, row.gradeBand, selectedRange) || row.urgencyTier !== null,
  );

  return visibleRows.sort((a, b) => {
    const severityDiff = severityOf(b.urgencyTier) - severityOf(a.urgencyTier);
    if (severityDiff !== 0) {
      return severityDiff;
    }
    return b.last30DaysLowCount - a.last30DaysLowCount;
  });
}
